import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query'

import { transformBodyByHttpMethod, transformResponseWithPagination } from '@helpers'
import { URLS } from '@constants'
import { CommandResponse, CommandTypeDTO, ExecuteCommandPayload, ObjectDataRecord } from '@types'

import { commonAPI } from './common.api'
import { responseFileHandler } from './config'

export const commandAPI = commonAPI.injectEndpoints({
  endpoints: build => ({
    executeCommand: build.mutation<CommandResponse[], ExecuteCommandPayload>({
      query: ({
        commandCode,
        objectCode,
        page,
        size,
        filter,
        expands,
        sort,
        params,
        body,
        method = 'POST',
      }) => ({
        url: `/command/objects/${objectCode}/${commandCode}`,
        method,
        params: {
          page,
          size,
          filter,
          sort,
          expands,
          ...params,
        },
        body: transformBodyByHttpMethod(method, body),
        responseHandler: responseFileHandler,
      }),
      transformResponse: (
        output: ObjectDataRecord[] | CommandResponse[],
        meta: FetchBaseQueryMeta,
        arg
      ) => {
        let result = output

        if (arg?.isCommandResponse) {
          result = output.map(response => {
            const { object, ...commandData } = response as CommandResponse

            return {
              ...object,
              command_response_data: commandData,
            }
          })
        }

        if (arg?.transformResponseWithPagination) {
          result = transformResponseWithPagination<ObjectDataRecord[]>(result, meta)
        }

        return result
      },
    }),
    executeCommandWithCache: build.query<CommandResponse[], ExecuteCommandPayload>({
      query: ({
        commandCode,
        objectCode,
        page,
        size,
        filter,
        expands,
        sort,
        params,
        body,
        method = 'POST',
      }) => ({
        url: `/command/objects/${objectCode}/${commandCode}`,
        method,
        params: {
          page,
          size,
          filter,
          sort,
          expands,
          ...params,
        },
        body: transformBodyByHttpMethod(method, body),
      }),
      transformResponse: (output: unknown[], meta: FetchBaseQueryMeta, arg) => {
        if (arg.transformResponseWithPagination) {
          return transformResponseWithPagination<unknown[]>(output, meta)
        }

        return output
      },
    }),
    executeCommandById: build.mutation<
      unknown[] | CommandResponse[],
      {
        commandId: string
        body: Record<string | number, unknown> | string
        params: Record<string | number, unknown>
        method?: string
      }
    >({
      query: ({ commandId, body, params, method = 'POST' }) => ({
        url: URLS.FRONT_API + `/command/${commandId}`,
        method,
        params,
        body,
        responseHandler: responseFileHandler,
      }),
      transformResponse: (
        output: ObjectDataRecord[] | CommandResponse[],
        meta: FetchBaseQueryMeta,
        arg
      ) => {
        if (arg?.isCommandResponse) {
          const commandResponse = output.map(response => {
            const { object, ...commandData } = response as CommandResponse

            return {
              ...object,
              command_response_data: commandData,
            }
          })

          return commandResponse
        }

        return output
      },
    }),
    fetchCommandsByObjectCode: build.query<CommandTypeDTO[], string | void>({
      query: objectCode => ({
        url: URLS.CORE_API + `/models/object/code/${objectCode}/commands`,
      }),
    }),
    fetchCommandParameters: build.query<
      CommandTypeDTO,
      { objectCode: string; commandCode: string; typeSource: string }
    >({
      query: ({ objectCode, commandCode, typeSource }) => ({
        url:
          URLS.CORE_API + `/models/${typeSource}/code/${objectCode}/commands/code/${commandCode}`,
      }),
    }),
    fetchCommandsBySystemCode: build.query<CommandTypeDTO[], string | void>({
      query: systemCode => ({
        url: URLS.CORE_API + `/models/system/code/${systemCode}/commands`,
      }),
    }),
  }),
})

export const {
  useExecuteCommandMutation,
  useLazyExecuteCommandWithCacheQuery,
  useExecuteCommandByIdMutation,
  useFetchCommandsByObjectCodeQuery,
  useFetchCommandParametersQuery,
  useFetchCommandsBySystemCodeQuery,
} = commandAPI
